import { useOverlay, useResponsive } from 'front-commons/hooks';
import { useEffect, useState } from 'react';
import type { UseHeaderBoniProps } from './interfaces';

/**
 * Coordinates the Header fidelity overlay and validation-page action.
 *
 * ---
 * 🧠 **Context**
 *
 * - Owns overlay expansion for the home Header entry point.
 * - Emits fidelity_program/header tracking before opening /validar-fidelidade in a new tab.
 *
 * ---
 * @example
 * ```ts
 * function useFidelityEntry(selectContentGTM: UseHeaderBoniProps['selectContentGTM']) {
 *   const fidelity = useHeaderBoni({ selectContentGTM });
 *   return { ...fidelity, showDetails: () => fidelity.setExpanded(true) };
 * }
 * ```
 *
 * ---
 * @function useHeaderBoni
 * @param {UseHeaderBoniProps} props - Tracking integration for fidelity navigation.
 * @returns {ReturnType<typeof useHeaderBoni>} Expansion controls, overlay presentation and validation action.
 */
export function useHeaderBoni({ selectContentGTM }: UseHeaderBoniProps) {
	const [expanded, setExpanded] = useState(false);

	const { medium } = useResponsive();
	const { handleShowOverlay, OverlayComponent } = useOverlay();

	function handleButtonNavigate() {
		selectContentGTM('fidelity_program', 'header');
		window.open('/validar-fidelidade', '_blank');
	}

	useEffect(() => {
		handleShowOverlay(expanded);
	}, [expanded]);

	return { expanded, setExpanded, medium, OverlayComponent, handleButtonNavigate };
}
